import { useEffect, useState } from 'react';

import { CircularProgress } from '@mui/material';
import { useAtom } from 'jotai';
import { useInView } from 'react-intersection-observer';
import type { RootStateOrAny } from 'react-redux';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import routesConstants from '@/routes/routes.constants';

import { useGetPractices } from '../../react-query/useGetPractices';
import { usePreferences } from '../../react-query/usePreferences';
import { useDebounce } from '../../utils/hooks/useDebounce';
import { mixpanelEvents, useTrackEvent } from '../../utils/mixpanel';
import { isViewingEngagementAtom } from '../insights/atoms';
import { isDirectorUser } from '../login/login-utils';

import { PracticeCard } from './PracticeCard';
import { SearchPracticeInput } from './SearchPracticesInput';
import { VideaInsights } from './VideaInsights';

// Temporary logo map
const logoMap = (organizationId: string) => {
  switch (organizationId) {
    case '639a612c083503a3d0006b5c':
      return 'assets/icons/temporaryLogos/Smile_Brands_Logo_white.svg';
    case '62debb72221910ff72be6094':
      return 'assets/icons/temporaryLogos/Riccobene_Dental_Logo_white.png';
    case '6216af044f5e3f4982c9afdf':
      return 'assets/icons/temporaryLogos/Heartland_Dental_Logo.svg';
    case '6228d418649cb7c66b2497cf':
      return 'assets/icons/temporaryLogos/42North_Dental_Logo_white.png';
    case '62cb614678302fa3a1d07643':
      return 'assets/icons/temporaryLogos/Lumio_Dental_Logo_white.png';
    case '63dacc98d0645c24f2b289f6':
      return 'assets/icons/temporaryLogos/elite_dental_logo_white.svg';
    case '6386431c262db1b3c765a782':
      return 'assets/icons/temporaryLogos/tend_Logo_white.png';
    default:
      return 'logo.svg';
  }
};

export const SimplifiedInsights = () => {
  const [search, setSearch] = useState('');
  const [isViewingEngagement, setIsViewingEngagement] = useAtom(
    isViewingEngagementAtom,
  );

  const { debouncedValue: debouncedSearch } = useDebounce(search);
  const user = useSelector((state: RootStateOrAny) => state?.login?.user);
  const history = useHistory();

  const { data: prefs, isLoading: isLoadingPreferences } = usePreferences(true);
  const { ref: bottomOfPageRef, inView: isBottomPracticeInView } = useInView();
  const {
    data: practices,
    fetchNextPage,
    isLoading,
  } = useGetPractices({
    practice: {
      name: debouncedSearch,
    },
    hideInSwitchboard: true,
  });
  const { nextCursor } = practices?.pages[practices.pages.length - 1] || {};
  useEffect(() => {
    if (nextCursor && isBottomPracticeInView && practices?.pages.length) {
      fetchNextPage();
    }
  }, [
    fetchNextPage,
    nextCursor,
    practices?.pages.length,
    isBottomPracticeInView,
  ]);
  const { trackEvent } = useTrackEvent();

  const practiceCards = practices?.pages
    ?.flatMap((practicePage) => practicePage.data)
    ?.map((practice) => <PracticeCard practice={practice} key={practice.id} />);

  useEffect(() => {
    trackEvent(mixpanelEvents.viewInsights, {
      screenName: 'Switchboard',
    });
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has a missing dependency: 'trackEvent'. Either include it or remove the dependency array. */
  }, []);

  const canAccessVideaInsights =
    !!prefs?.enableInsights &&
    prefs?.enableInsights?.length > 0 &&
    isDirectorUser(user);

  useEffect(() => {
    if (
      canAccessVideaInsights &&
      prefs?.enableInsights &&
      prefs?.enableInsights.includes('ENGAGEMENT') &&
      (!prefs?.enableInsightsPOC ||
        prefs?.enableInsights?.filter((insight) => insight !== 'ENGAGEMENT')
          .length === 0)
    ) {
      setIsViewingEngagement(true);
    }
  }, [
    canAccessVideaInsights,
    prefs?.enableInsightsPOC,
    prefs?.enableInsights,
    setIsViewingEngagement,
  ]);

  useEffect(() => {
    if (
      canAccessVideaInsights &&
      prefs?.enableInsightsPOC &&
      prefs?.enableInsights &&
      prefs?.enableInsights?.filter((insight) => insight !== 'ENGAGEMENT')
        .length > 0 &&
      !isViewingEngagement
    ) {
      history.push(routesConstants.INSIGHTS);
    }
  }, [
    canAccessVideaInsights,
    prefs?.enableInsightsPOC,
    prefs?.enableInsights,
    isViewingEngagement,
    history,
  ]);

  if (!user.primaryOrganizationId || isLoadingPreferences) {
    return null;
  }

  if (canAccessVideaInsights) {
    return <VideaInsights />;
  }

  const logo = logoMap(user.primaryOrganizationId);

  return (
    <div className="vai-w-full vai-flex vai-flex-col vai-items-center vai-gap-4">
      <div className="vai-w-full vai-flex vai-flex-col vai-items-center">
        <img
          src={logo}
          className={`
            vai-m-4
            vai-min-w-8
            vai-min-h-20
            vai-max-h-32
            ${
              logo === 'logo.svg'
                ? 'vai-max-h-20 vai-max-w-40 vai-min-w-40 vai-mb-8'
                : ''
            }
          `}
          alt="Organization Logo"
        />
        <SearchPracticeInput search={search} setSearch={setSearch} />
      </div>
      {isLoading ? (
        <CircularProgress data-testid="loading-spinner" />
      ) : (
        <div
          
          data-testid="practices-table"
          className="vai-grid vai-grid-cols-5 vai-gap-4"
        >
          {practiceCards}
          {practiceCards && <div ref={bottomOfPageRef} />}
        </div>
      )}
    </div>
  );
};
